import {
  AbstractControl,
  Validators,
  FormGroup,
} from "@angular/forms";
import { constants } from "./constants";
import { Role } from "../models/role.model";
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer'; 


/**
 * Manejador de cambio en el tipo de documento seleccionado
 */
const onChangeDocType = (event: any, control: AbstractControl): number => {
  let maxLength: number;
  let minLength: number;
  const pattern: string = constants.validations.numeric.pattern;
  if (event.target.value === "DNI") {
    maxLength = 8;
    minLength = 8;
  } else {
    maxLength = 11;
    minLength = 4;
  }
  control.setValidators([
    Validators.required,
    Validators.pattern(pattern),
    Validators.minLength(minLength),
    Validators.maxLength(maxLength),
  ]);
  control.enable();

  // Validations
  if (control.value?.length > maxLength) {
    setTimeout(() => {
      control.markAsTouched();
    }, 0);
  }

  return maxLength;
};

/**
 * Obtener estado de input para has-error class
 */
const invalidControl = (control: AbstractControl): boolean => {
  return control.touched && control.invalid;
};

/**
 * Inicializar listeners para todos los FormControl de un FormGroup
 * con el objetivo de obtener el comportamiendo deseado
 */
const initFormListeners = (form: FormGroup): void => {
  Object.keys(form.controls).forEach((key) => {
    const control = form.get(key);
    control.valueChanges.subscribe(() => {
      if (control.touched) {
        control.markAsUntouched();
      }
    });
  });
};

/**
 * Procesa roleTypes recibidos de DirectorProfiling y los mapea al modelo de roles manejado por el canal
 */
const processRoles = (roleTypes: any): Role[] => {
  let roles = [];
  try {
    roleTypes.roleType.forEach((role: any) =>
      roles.push(new Role(role.roleTypeId, role?.longDesc))
    );
  } catch (error) {
    roles.push(
      new Role(roleTypes?.roleType?.roleTypeId, roleTypes?.roleType?.longDesc)
    );
  }
  return roles;
};

/**
 * Procesa securityItems recibidos de DirectorProfiling y los mapea permisos manejados por el canal
 */
const processPermissions = (securityItems: any): any[] => {
  let permissions = [];
  try {
    securityItems.securityItem.forEach((item: any) =>
      permissions.push(parseInt(item.securityItemId.split("@")[1]))
    );
  } catch (error) {
    permissions.push(
      parseInt(securityItems.securityItem.securityItemId.split("@")[1])
    );
  }
  return permissions;
};

/**
 * Obtiene Date del formato interno de fechas 2020 10 23 23 45 58 000-0400
 */
const dateFromInternalFormat = (dateString: string): Date =>
  new Date(
    parseInt(dateString.slice(0, 4)),
    parseInt(dateString.slice(4, 6)) - 1,
    parseInt(dateString.slice(6, 8)),
    parseInt(dateString.slice(8, 10)),
    parseInt(dateString.slice(10, 12))
  );

/**
 * Obtiene formato interno de fechas a partir de un Date
 */
const internalFormatFromDate = (date: Date): string => {
  const month = date.getMonth() + 1;
  const hour = date.getHours() + 1;
  var d = date
  .getFullYear()
  .toString()
  .concat(month.toString().padStart(2, "0"))
  .concat(date.getDate().toString().padStart(2, "0"))
  .concat(hour.toString().padStart(2, "0"))
  .concat(date.getMinutes().toString().padStart(2, "0"))
  .concat(date.getSeconds().toString().padStart(2, "0"))
  .concat("000-0400");

  return d;
};

/**
 * Obtiene Date del formato recibido en los comprobantes: 28/07/2020 12:00:00
 */
const dateFromReceipt = (dateString: string): Date => {
  const parts = dateString.split(" ")[0].split("/");
  return new Date(
    parseInt(parts[2]),
    parseInt(parts[1]) - 1,
    parseInt(parts[0])
  );
};

/**
 * Convierte Date al formato requerido en los comprobantes: 28/07/2020 12:00:00
 */
const dateToReceipt = (date: Date): string => {
  const month = date.getMonth() + 1;
  return date
    .getDate()
    .toString()
    .padStart(2, "0")
    .concat("/")
    .concat(month.toString().padStart(2, "0"))
    .concat("/")
    .concat(date.getFullYear().toString())
    .concat(" ")
    .concat(date.getHours().toString().padStart(2, "0"))
    .concat(":")
    .concat(date.getMinutes().toString().padStart(2, "0"))
    .concat(":")
    .concat(date.getSeconds().toString().padStart(2, "0"));
};

/**
 * Helper para convertir un string a titlecase
 */
const toTitleCase = (str: string) => {
  return str.replace(
    /\w\S*/g,
    function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
}

/**
 * Helper para determinar si la respuesta de la consulta masiva es vacía - {}
 */
const emptyResponse = (response: any): boolean => {
  return Object.keys(response).length === 0 && response.constructor === Object;
};

/**
 * Helper para determinar si la extension de un archivo es .txt
 */
const isTxt = (file: File): boolean => {
  return file.type === "" || file.type === undefined
    ? false
    : file.type.startsWith("text");
};

/**
 * Helper para determinar si la extension de un archivo es .pdf
 */
const isPdf = (file: File): boolean => {
  return file.type === "" || file.type === undefined
    ? false
    : file.type.startsWith("application/pdf");
};

const isPdfEmpty = (file: File): boolean => {
  return (file.size > 10000);
  
};

/**
 * Helper para determinar si el peso de un archivo supera al maximo esperado
 */
const oversizeFile = (file: File, maxSizeMegaBytes: number) => {
  return file.size / (1024 * 1024) > maxSizeMegaBytes;
};

/**
 * Helper para determinar si la fecha de transferencia es válida
 * utilizado en el CRUD de comprobantes
 */
const validDate = (dateControl: AbstractControl): boolean => {
  try {
    return dateControl.value.getTime() !== 0;
  } catch (e) {
    return false;
  }
};

/**
 * Helper para determinar si el monto ingresado es 0
 * utilizado en el CRUD de comprobantes
 */
const invalidAmount = (
  amountControl: AbstractControl
): { [s: string]: boolean } => {
   
  if (isNaN(amountControl.value)) return null;
  return parseFloat("0.00") === parseFloat(amountControl.value)   ? { invalidAmount: true } : null;
};

// =================================================================
//                          Exports
// =================================================================
const helpers = {
  onChangeDocType,
  invalidControl,
  initFormListeners,
  processRoles,
  processPermissions,
  dateFromInternalFormat,
  internalFormatFromDate,
  emptyResponse,
  isTxt,
  isPdf,
  isPdfEmpty,
  oversizeFile,
  dateFromReceipt,
  dateToReceipt,
  validDate,
  invalidAmount,
  toTitleCase,
};
export default helpers;
